import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePlanAccess } from '@/hooks/usePlanAccess';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles } from 'lucide-react';

interface PlanRouteGuardProps {
  children: React.ReactNode;
  requirePremium?: boolean;
}

export const PlanRouteGuard: React.FC<PlanRouteGuardProps> = ({ 
  children, 
  requirePremium = false 
}) => {
  const { plan, loading, hasPremiumAccess, hasStatsAccess } = usePlanAccess();
  
  if (loading) {
    return <LoadingSpinner message="Vérification du plan..." />;
  }
  
  // Si aucun plan n'est sélectionné, rediriger vers la page d'abonnement
  if (!plan) {
    return <Navigate to="/subscription" replace />;
  }
  
  // Si la route nécessite Premium mais l'utilisateur a seulement Stats
  if (requirePremium && !hasPremiumAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Lock className="w-8 h-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl">Fonctionnalité Premium</CardTitle>
            <CardDescription>
              Cette fonctionnalité nécessite un abonnement Premium pour y accéder.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-medium">Votre plan actuel : Chart (4,90€/mois)</p>
              <p className="text-sm text-muted-foreground">
                Passez au plan Premium pour débloquer :
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• Gestion complète de la caisse</li>
                <li>• Gestion des retraits de stock</li>
                <li>• Analytiques avancées</li>
                <li>• Historique complet</li>
                <li>• Gestion d'équipe</li>
                <li>• Export/Import de données</li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => window.location.href = '/subscription'} 
                className="flex-1"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Passer au Premium
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/statistics'}
                className="flex-1"
              >
                Retour aux tableaux
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return <>{children}</>;
};
